import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    List,
    Activity,
    UserPlus,
    Target,
    Globe,
    FileText
} from 'lucide-react';
import LeadDashboard from './LeadDashboard';
import LeadListFilters from './LeadListFilters';
import LeadActivities from './LeadActivities';
import LeadAssignment from './LeadAssignment';
import LeadScoringQualification from './LeadScoringQualification';
import LeadSourcesCapture from './LeadSourcesCapture';
// import TenderLead from './TenderLead';
import Drawer from '../../../components/common/Drawer';
import ManualLeadEntry from './ManualLeadEntry';
import LeadDetail360 from './LeadDetail360';
import LogActivity from './LogActivity';

const LeadManagementModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for tabs
    const activeTab = searchParams.get('tab') || 'dashboard';

    // State for drawers
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        // Open drawer if action is present in URL
        if (action) {
            setDrawerOpen(true);
        } else {
            setDrawerOpen(false);
        }
    }, [action]);

    const handleTabChange = (tabKey) => {
        setSearchParams({ tab: tabKey });
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        // Remove action and id from URL but keep the tab
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        newParams.delete('mode'); // Clear mode if any (e.g. edit)
        setSearchParams(newParams);
    };

    // Drawer Content Logic
    const renderDrawerContent = () => {
        switch (action) {
            case 'add_lead':
                return (
                    <ManualLeadEntry
                        onBack={closeDrawer}
                        onSuccess={() => {
                            closeDrawer();
                            // Optionally trigger a refresh if needed, but react-query often handles this
                        }}
                    />
                );
            case 'edit_lead':
                return (
                    <ManualLeadEntry
                        leadId={actionId}
                        onBack={closeDrawer}
                        onSuccess={() => {
                            closeDrawer();
                        }}
                    />
                );
            case 'view_lead':
                return (
                    <LeadDetail360
                        leadId={actionId}
                        onBack={closeDrawer}
                        onEdit={handleEditLead}
                    />
                );
            case 'log_activity': // For adding new activity to a lead
                return (
                    <LogActivity
                        leadId={actionId}
                        onBack={closeDrawer}
                        onSuccess={closeDrawer}
                    />
                );
            case 'edit_activity':
                return (
                    <LogActivity
                        activityId={actionId}
                        onBack={closeDrawer}
                        onSuccess={closeDrawer}
                    />
                );
            case 'add_activity_global': // Log activity without pre-selected lead (if supported)
                return (
                    <LogActivity
                        onBack={closeDrawer}
                        onSuccess={closeDrawer}
                    />
                );
            default:
                return null;
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'add_lead': return 'Add New Lead';
            case 'edit_lead': return 'Edit Lead';
            case 'view_lead': return 'Lead Details';
            case 'log_activity': return 'Log Activity';
            case 'edit_activity': return 'Edit Activity';
            default: return '';
        }
    };

    // Action Handlers for Child Components
    const handleViewLead = (leadId) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('action', 'view_lead');
            next.set('id', leadId);
            return next;
        });
    };

    const handleEditLead = (leadId) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('action', 'edit_lead');
            next.set('id', leadId);
            return next;
        });
    };

    const handleAddLead = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('action', 'add_lead');
            next.delete('id');
            return next;
        });
    };

    const handleLogActivity = (leadId) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('action', 'log_activity');
            next.set('id', leadId);
            return next;
        });
    };

    const handleAssignLead = (leadId) => {
        // For assignment, we might want to navigate to the assignment tab or open a specific drawer
        // For now, let's switch to the Assignment tab
        setSearchParams({ tab: 'assignment' });
        // Ideally passing the leadId to filter/highlight would be good, implement later
    };

    const tabs = [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, component: LeadDashboard },
        { key: 'list', label: 'Lead List', icon: List, component: LeadListFilters },
        { key: 'activities', label: 'Activities', icon: Activity, component: LeadActivities },
        { key: 'assignment', label: 'Assignment', icon: UserPlus, component: LeadAssignment },
        { key: 'scoring', label: 'Scoring & Qual.', icon: Target, component: LeadScoringQualification },
        { key: 'sources', label: 'Sources', icon: Globe, component: LeadSourcesCapture },
//         { key: 'tenders', label: 'Tenders', icon: FileText, component: TenderLead },
    ];

    return (
        <div className="h-full flex flex-col">
            {/* Tab Navigation */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex space-x-8 overflow-x-auto scrollbar-hide" aria-label="Tabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => handleTabChange(tab.key)}
                                    className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                    ${isActive
                                            ? 'border-agrosaf-green text-agrosaf-green'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                  `}
                                >
                                    <Icon className={`h-4 w-4 ${isActive ? 'text-agrosaf-green' : 'text-gray-400'}`} />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto bg-gray-50 p-4 sm:p-6 lg:p-8">
                {activeTab === 'dashboard' && <LeadDashboard onViewLead={handleViewLead} onAddLead={handleAddLead} />}
                {activeTab === 'list' && (
                    <LeadListFilters
                        onViewLead={handleViewLead}
                        onEditLead={handleEditLead}
                        onAssignLead={handleAssignLead}
                        onAddLead={handleAddLead}
                        onLogActivity={handleLogActivity}
                    />
                )}
                {activeTab === 'activities' && <LeadActivities onViewLead={handleViewLead} />}
                {activeTab === 'assignment' && <LeadAssignment onViewLead={handleViewLead} />}
                {activeTab === 'scoring' && <LeadScoringQualification />}
                {activeTab === 'sources' && <LeadSourcesCapture />}
                {/* {activeTab === 'tenders' && <TenderLead />} */}
            </div>

            {/* Drawer */}
            <Drawer
                isOpen={drawerOpen}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                className="w-full max-w-4xl" // Adjust width as needed
            >
                {drawerOpen && renderDrawerContent()}
            </Drawer>
        </div>
    );
};

export default LeadManagementModule;
